import { openDB } from "idb";

const DB_NAME = "dd-offline";
const STORE = "pending_photos";
const PAIR_STORE = "pairing_pending";

let _dbPromise = null;
function db() {
  if (!_dbPromise) {
    _dbPromise = openDB(DB_NAME, 3, {
      upgrade(d, oldVersion, newVersion, transaction) {
        let store;
        if (!d.objectStoreNames.contains(STORE)) {
          store = d.createObjectStore(STORE, { keyPath: "client_upload_id" });
        } else {
          // idb passes the live versionchange transaction as the 4th arg — use it (NOT
          // d.transaction, which is the factory method) to reach the existing store.
          store = transaction.objectStore(STORE);
        }
        // v2: account isolation — index queued uploads by their owner so one account never
        // drains or sees another account's pending evidence (Feature 17).
        if (!store.indexNames.contains("by_owner")) {
          store.createIndex("by_owner", "owner_user_id");
        }
        // v3: Same-View pairing that survives reload — minimal workflow metadata only (NO image
        // Blob, NO tokens, NO server responses, NO private media URLs). Owner-scoped.
        if (!d.objectStoreNames.contains(PAIR_STORE)) {
          const ps = d.createObjectStore(PAIR_STORE, { keyPath: "id" });
          ps.createIndex("by_owner", "owner_user_id");
        }
      },
    });
  }
  return _dbPromise;
}

export async function addPending(record) {
  const d = await db();
  await d.put(STORE, record);
}

export async function getAllPending(ownerId) {
  let d;
  try {
    d = await db();
  } catch {
    // IndexedDB unavailable (private mode / disabled / open error): fail soft. Callers treat an
    // empty queue as "nothing durably persisted" — captures fall back to memory-only recovery.
    return [];
  }
  let all;
  try {
    all = ownerId
      ? await d.getAllFromIndex(STORE, "by_owner", ownerId)
      : await d.getAll(STORE);
  } catch {
    // Defensive: if the by_owner index is ever missing, fail soft with a full-scan filter
    // rather than taking the whole capture pipeline down.
    try {
      const raw = await d.getAll(STORE);
      all = ownerId ? raw.filter((r) => r.owner_user_id === ownerId) : raw;
    } catch {
      return [];
    }
  }
  // oldest first
  return all.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
}

export async function updatePending(id, patch) {
  const d = await db();
  const rec = await d.get(STORE, id);
  if (!rec) return;
  await d.put(STORE, { ...rec, ...patch });
}

export async function deletePending(id) {
  const d = await db();
  await d.delete(STORE, id);
}

// ---------------- Same-View pairing-pending (v3) ----------------
export async function addPairingPending(record) {
  const d = await db();
  await d.put(PAIR_STORE, record);
}

export async function getPairingPending(ownerId) {
  let d;
  try {
    d = await db();
  } catch {
    return []; // IndexedDB unavailable — fail soft (no pairing prompts rather than a crash).
  }
  let all;
  try {
    all = ownerId
      ? await d.getAllFromIndex(PAIR_STORE, "by_owner", ownerId)
      : await d.getAll(PAIR_STORE);
  } catch {
    try {
      const raw = await d.getAll(PAIR_STORE);
      all = ownerId ? raw.filter((r) => r.owner_user_id === ownerId) : raw;
    } catch {
      return [];
    }
  }
  return all.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
}

export async function updatePairingPending(id, patch) {
  const d = await db();
  const rec = await d.get(PAIR_STORE, id);
  if (!rec) return;
  await d.put(PAIR_STORE, { ...rec, ...patch });
}

export async function deletePairingPending(id) {
  const d = await db();
  await d.delete(PAIR_STORE, id);
}

// Compress an image File/Blob to a smaller JPEG Blob. Retained for callers that want a
// lightweight copy; the resilient capture queue stores the EXACT accepted bytes instead so
// the server-side SHA-256 custody hash matches what the user captured.
export function compressImage(file, maxDim = 1600, quality = 0.7) {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const { width, height } = img;
        const ratio = Math.min(1, maxDim / Math.max(width, height));
        const w = Math.round(width * ratio);
        const h = Math.round(height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        canvas.toBlob(
          (blob) => resolve(blob || file),
          "image/jpeg",
          quality,
        );
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    } catch {
      resolve(file);
    }
  });
}
